import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type FieldErrors } from "react-hook-form";
import { Link, useNavigate, useSearchParams } from "react-router";

import {
  Alert,
  Button,
  Card,
  LoadingBlock,
  PageHeader,
  SelectField,
  TextArea,
  TextField,
} from "../../components/ui";
import { useToast } from "../../components/Toast";
import { ApiError, api, applyFieldErrors, errorMessage } from "../../lib/api";
import { useConfig } from "../../lib/config";
import { formatBytes, todayInZone } from "../../lib/format";
import type { Option } from "../../lib/types";
import { FileUploader, type AttachedFile } from "./FileUploader";
import { isShipping, orderSchema, toPayload, type OrderFormValues } from "./orderSchema";
import type { Membership, OrderDetail, Profile } from "./types";

function labelOf(options: Option[] | undefined, value: string | undefined) {
  return options?.find((o) => o.value === value)?.label ?? value ?? "";
}

function Fieldset({ legend, children }: { legend: string; children: React.ReactNode }) {
  return (
    <fieldset className="rounded-lg border border-line bg-surface p-5">
      <legend className="px-1 text-lg font-bold">{legend}</legend>
      <div className="mt-2 grid items-start gap-4 sm:grid-cols-2">{children}</div>
    </fieldset>
  );
}

const Full = ({ children }: { children: React.ReactNode }) => (
  <div className="sm:col-span-2">{children}</div>
);

export function NewOrderPage() {
  const { data: config } = useConfig();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [params] = useSearchParams();
  const [step, setStep] = useState<"form" | "review">("form");
  const [files, setFiles] = useState<AttachedFile[]>([]);
  const [fileError, setFileError] = useState<string | undefined>();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [draftId, setDraftId] = useState<string | null>(params.get("draft"));
  const duplicateId = params.get("duplicate");
  const [duplicateNotice, setDuplicateNotice] = useState<{
    number: string;
    unavailable: string[];
  } | null>(null);
  const idempotencyKey = useRef(crypto.randomUUID());

  const profile = useQuery({ queryKey: ["profile"], queryFn: () => api.get<Profile>("/profile") });
  const memberships = useQuery({
    queryKey: ["memberships"],
    queryFn: () => api.get<Membership[]>("/companies/mine"),
  });
  const companies = useMemo(
    () =>
      (memberships.data ?? []).filter(
        (m) => m.status === "approved" && m.company.status === "active",
      ),
    [memberships.data],
  );

  const tz = config?.business_timezone ?? "America/Los_Angeles";
  const today = todayInZone(tz);

  const form = useForm<OrderFormValues>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      ship_use_customer_address: true,
      due_time: "",
      delivery_method: "will_call",
      binding: "none",
    },
  });
  const { register, handleSubmit, control, reset, getValues, setError, formState } = form;
  const errors = formState.errors;

  // Prefill from profile (contact details are snapshotted into the order on submit)
  useEffect(() => {
    if (!profile.data || draftId || duplicateId) return;
    const p = profile.data;
    reset((v) => ({
      ...v,
      customer_name: p.customer_name,
      customer_phone: p.phone ?? "",
      customer_email: p.contact_email ?? p.email,
      customer_street: p.street ?? "",
      customer_city: p.city ?? "",
      customer_state: p.state ?? "",
      customer_zip: p.zip_code ?? "",
      customer_company_name: p.company_name ?? "",
      delivery_method: p.default_delivery_method ?? "will_call",
      payment_method: p.default_payment_method ?? "",
    }));
  }, [profile.data, reset, draftId, duplicateId]);

  // Load a saved draft
  useEffect(() => {
    if (!draftId) return;
    api
      .get<{ id: string; data: Record<string, unknown> }[]>("/orders/drafts")
      .then(async (drafts) => {
        const d = drafts.find((x) => x.id === draftId);
        if (!d) return;
        const { upload_ids, ...values } = d.data as { upload_ids?: string[] } & Record<
          string,
          unknown
        >;
        reset({ ship_use_customer_address: true, ...(values as Partial<OrderFormValues>) });
        const restored: AttachedFile[] = [];
        for (const id of upload_ids ?? []) {
          try {
            const u = await api.get<{
              id: string;
              filename: string;
              size: number;
              state: string;
              message: string | null;
            }>(`/uploads/${id}`);
            restored.push({
              key: u.id,
              name: u.filename,
              size: u.size,
              progress: {
                state:
                  u.state === "ready" ? "ready" : u.state === "checking" ? "checking" : "rejected",
                uploadId: u.id,
                sent: u.size,
                total: u.size,
                message:
                  u.state === "ready" ? undefined : (u.message ?? "Please attach this file again."),
              },
            });
          } catch {
            /* upload no longer exists */
          }
        }
        setFiles(restored);
      })
      .catch(() => undefined);
  }, [draftId, reset]);

  // Duplicate a previous order: copy every field and its files; due date is chosen fresh.
  const duplicateStarted = useRef(false);
  useEffect(() => {
    if (!duplicateId || duplicateStarted.current) return;
    duplicateStarted.current = true;
    (async () => {
      try {
        const o = await api.get<OrderDetail>(`/orders/${duplicateId}`);
        const shipping = isShipping(o.delivery_method);
        reset({
          company_id: o.owning_company_id ?? "",
          customer_name: o.customer_name,
          customer_phone: o.customer_phone,
          customer_email: o.customer_email,
          customer_street: o.customer_street,
          customer_city: o.customer_city,
          customer_state: o.customer_state,
          customer_zip: o.customer_zip,
          customer_company_name: o.customer_company_name ?? "",
          due_date: "",
          due_time: "",
          delivery_method: o.delivery_method,
          ship_use_customer_address: !shipping,
          ship_recipient: shipping ? (o.ship_recipient ?? "") : "",
          ship_street: shipping ? (o.ship_street ?? "") : "",
          ship_city: shipping ? (o.ship_city ?? "") : "",
          ship_state: shipping ? (o.ship_state ?? "") : "",
          ship_zip: shipping ? (o.ship_zip ?? "") : "",
          ship_instructions: o.ship_instructions ?? "",
          project_name_number: o.project_name_number,
          payment_method: o.payment_method,
          department: o.department,
          originals_count: o.originals_count,
          printed_sets: o.printed_sets,
          print_size: o.print_size,
          binding: o.binding,
          binding_other: o.binding_other ?? "",
          notes: o.notes ?? "",
        });
        const copied = await api.post<{
          uploads: { id: string; filename: string; size: number }[];
          unavailable: string[];
        }>(`/orders/${duplicateId}/duplicate-files`);
        setFiles(
          copied.uploads.map((u) => ({
            key: u.id,
            name: u.filename,
            size: u.size,
            progress: { state: "ready", uploadId: u.id, sent: u.size, total: u.size },
          })),
        );
        setDuplicateNotice({ number: o.order_number, unavailable: copied.unavailable });
      } catch (err) {
        setSubmitError(`We could not copy that order: ${errorMessage(err)}`);
      }
    })();
  }, [duplicateId, reset]);

  const delivery = useWatch({ control, name: "delivery_method" });
  const useCustomerAddress = useWatch({ control, name: "ship_use_customer_address" });
  const binding = useWatch({ control, name: "binding" });
  const readyIds = files
    .filter((f) => f.progress.state === "ready" && f.progress.uploadId)
    .map((f) => f.progress.uploadId!);
  const busyFiles = files.some((f) =>
    ["queued", "uploading", "finishing", "checking"].includes(f.progress.state),
  );

  const goReview = handleSubmit(
    () => {
      setFileError(undefined);
      if (busyFiles)
        return setFileError("Please wait until all files finish uploading and checking.");
      if (files.some((f) => f.progress.state !== "ready"))
        return setFileError("Remove files that were not accepted before continuing.");
      if (readyIds.length === 0) return setFileError("Attach at least one file to your order.");
      setStep("review");
      window.scrollTo(0, 0);
    },
    (errs: FieldErrors) => {
      const first = Object.keys(errs)[0];
      if (first) document.getElementsByName(first)[0]?.focus();
    },
  );

  const saveDraft = async () => {
    const body = { data: { ...getValues(), upload_ids: readyIds } };
    try {
      if (draftId) await api.put(`/orders/drafts/${draftId}`, body);
      else setDraftId((await api.post<{ id: string }>("/orders/drafts", body)).id);
      notify("Draft saved. You can finish it later from Orders TO Bill's.");
    } catch (err) {
      notify(errorMessage(err), "error");
    }
  };

  const submit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const order = await api.post<OrderDetail>(
        "/orders",
        toPayload(getValues(), readyIds, draftId),
        {
          headers: { "Idempotency-Key": idempotencyKey.current },
        },
      );
      navigate(`/orders/${order.id}/submitted`, {
        replace: true,
        state: { orderNumber: order.order_number },
      });
    } catch (err) {
      setStep("form");
      if (applyFieldErrors(err, setError)) setSubmitError("Please correct the highlighted fields.");
      else if (err instanceof ApiError && err.fields[0]?.field === "upload_ids")
        setFileError(err.fields[0].message);
      else setSubmitError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (!config || profile.isLoading) return <LoadingBlock />;
  const o = config.options;
  const v = getValues();

  if (step === "review")
    return (
      <div className="flex flex-col gap-6">
        <PageHeader
          title="Review your order"
          description="Check the details below, then submit your order to Bill's."
        />
        <Card>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            {[
              ["Customer", `${v.customer_name} · ${v.customer_phone} · ${v.customer_email}`],
              [
                "Address",
                `${v.customer_street}, ${v.customer_city}, ${v.customer_state} ${v.customer_zip}`,
              ],
              [
                "Ordering for",
                v.company_id
                  ? companies.find((c) => c.company.id === v.company_id)?.company.display_name
                  : "Myself",
              ],
              [
                "Requested due",
                `${v.due_date} at ${labelOf(o.due_times, v.due_time)} (Bill's will confirm)`,
              ],
              ["Delivery", labelOf(o.delivery_methods, v.delivery_method)],
              ...(isShipping(v.delivery_method)
                ? [
                    [
                      "Deliver to",
                      v.ship_use_customer_address
                        ? "Customer address above"
                        : `${v.ship_recipient}, ${v.ship_street}, ${v.ship_city}, ${v.ship_state} ${v.ship_zip}`,
                    ],
                  ]
                : []),
              ["Project", v.project_name_number],
              ["Payment", labelOf(o.payment_methods, v.payment_method)],
              ["Department", labelOf(o.departments, v.department)],
              [
                "Originals / sets",
                `${v.originals_count} originals, ${v.printed_sets} printed sets`,
              ],
              ["Size", labelOf(o.print_sizes, v.print_size)],
              [
                "Binding",
                v.binding === "other"
                  ? `Other: ${v.binding_other}`
                  : labelOf(o.bindings, v.binding),
              ],
              ["Notes", v.notes || "None"],
            ].map(([k, val]) => (
              <div key={k}>
                <dt className="text-sm font-semibold text-ink-muted">{k}</dt>
                <dd>{val}</dd>
              </div>
            ))}
          </dl>
          <h2 className="mt-6 font-bold">Files ({files.length})</h2>
          <ul className="mt-2 list-disc pl-5">
            {files.map((f) => (
              <li key={f.key}>
                {f.name} ({formatBytes(f.size)})
              </li>
            ))}
          </ul>
        </Card>
        {submitError && <Alert tone="error">{submitError}</Alert>}
        <div className="flex flex-wrap gap-3">
          <Button variant="secondary" onClick={() => setStep("form")}>
            Go back and edit
          </Button>
          <Button busy={submitting} onClick={submit}>
            Submit order
          </Button>
        </div>
      </div>
    );

  return (
    <form noValidate onSubmit={goReview} className="flex flex-col gap-6">
      <PageHeader
        title="New order TO Bill's"
        description="Fields marked * are required. Your requested due date is confirmed by Bill's after you submit."
      />
      {submitError && <Alert tone="error">{submitError}</Alert>}
      {duplicateNotice && (
        <Alert tone="info" title={`Copied from order ${duplicateNotice.number}`}>
          Everything was filled in from that order, including its files. Choose a new due date and
          change anything else before you submit. This will be a new order with its own number.
          {duplicateNotice.unavailable.length > 0 && (
            <span className="mt-1 block">
              These files are no longer stored and were not copied:{" "}
              {duplicateNotice.unavailable.join(", ")}. Attach them again if you still need them.
            </span>
          )}
        </Alert>
      )}

      <Fieldset legend="Customer information">
        {companies.length > 0 && (
          <Full>
            <SelectField
              label="Ordering for"
              placeholder="Myself (individual order)"
              options={companies.map((c) => ({
                value: c.company.id,
                label: c.company.display_name,
              }))}
              hint="Company orders are visible to everyone approved in that company."
              {...register("company_id")}
            />
          </Full>
        )}
        <TextField
          label="Customer name"
          required
          autoComplete="name"
          error={errors.customer_name?.message}
          {...register("customer_name")}
        />
        <TextField
          label="Company name"
          autoComplete="organization"
          error={errors.customer_company_name?.message}
          {...register("customer_company_name")}
        />
        <TextField
          label="Phone"
          type="tel"
          required
          autoComplete="tel"
          error={errors.customer_phone?.message}
          {...register("customer_phone")}
        />
        <TextField
          label="Email"
          type="email"
          required
          autoComplete="email"
          error={errors.customer_email?.message}
          {...register("customer_email")}
        />
        <Full>
          <TextField
            label="Street address"
            required
            autoComplete="street-address"
            error={errors.customer_street?.message}
            {...register("customer_street")}
          />
        </Full>
        <TextField
          label="City"
          required
          autoComplete="address-level2"
          error={errors.customer_city?.message}
          {...register("customer_city")}
        />
        <div className="grid grid-cols-2 gap-4">
          <TextField
            label="State"
            required
            autoComplete="address-level1"
            error={errors.customer_state?.message}
            {...register("customer_state")}
          />
          <TextField
            label="ZIP"
            required
            inputMode="numeric"
            autoComplete="postal-code"
            error={errors.customer_zip?.message}
            {...register("customer_zip")}
          />
        </div>
      </Fieldset>

      <Fieldset legend="Time constraint">
        <TextField
          label="Requested due date"
          type="date"
          required
          min={today}
          hint={`Dates and times are Bill's local time (${tz.replace("_", " ")}).`}
          error={errors.due_date?.message}
          {...register("due_date")}
        />
        <SelectField
          label="Requested due time"
          required
          options={o.due_times}
          error={errors.due_time?.message}
          {...register("due_time")}
        />
      </Fieldset>

      <Fieldset legend="Delivery">
        <Full>
          <SelectField
            label="Delivery method"
            required
            options={o.delivery_methods}
            placeholder="Choose one"
            error={errors.delivery_method?.message}
            {...register("delivery_method")}
          />
        </Full>
        {isShipping(delivery) && (
          <>
            <Full>
              <label className="flex items-center gap-2 font-medium">
                <input
                  type="checkbox"
                  className="h-5 w-5"
                  {...register("ship_use_customer_address")}
                />
                Use the customer address above
              </label>
            </Full>
            {!useCustomerAddress && (
              <>
                <Full>
                  <TextField
                    label="Recipient"
                    required
                    error={errors.ship_recipient?.message}
                    {...register("ship_recipient")}
                  />
                </Full>
                <Full>
                  <TextField
                    label="Street address"
                    required
                    error={errors.ship_street?.message}
                    {...register("ship_street")}
                  />
                </Full>
                <TextField
                  label="City"
                  required
                  error={errors.ship_city?.message}
                  {...register("ship_city")}
                />
                <div className="grid grid-cols-2 gap-4">
                  <TextField
                    label="State"
                    required
                    error={errors.ship_state?.message}
                    {...register("ship_state")}
                  />
                  <TextField
                    label="ZIP"
                    required
                    inputMode="numeric"
                    error={errors.ship_zip?.message}
                    {...register("ship_zip")}
                  />
                </div>
              </>
            )}
            <Full>
              <TextArea
                label="Delivery instructions"
                rows={2}
                error={errors.ship_instructions?.message}
                {...register("ship_instructions")}
              />
            </Full>
          </>
        )}
      </Fieldset>

      <Fieldset legend="Accounting details">
        <TextField
          label="Project name or number"
          required
          error={errors.project_name_number?.message}
          {...register("project_name_number")}
        />
        <SelectField
          label="Payment"
          required
          options={o.payment_methods}
          error={errors.payment_method?.message}
          {...register("payment_method")}
        />
      </Fieldset>

      <Fieldset legend="Production specifics">
        <SelectField
          label="Department"
          required
          options={o.departments}
          error={errors.department?.message}
          {...register("department")}
        />
        <SelectField
          label="Size"
          required
          options={o.print_sizes}
          error={errors.print_size?.message}
          {...register("print_size")}
        />
        <TextField
          label="Number of originals"
          type="number"
          min={1}
          step={1}
          required
          inputMode="numeric"
          error={errors.originals_count?.message}
          {...register("originals_count")}
        />
        <TextField
          label="Total printed sets"
          type="number"
          min={1}
          step={1}
          required
          inputMode="numeric"
          error={errors.printed_sets?.message}
          {...register("printed_sets")}
        />
        <SelectField
          label="Binding"
          required
          options={o.bindings}
          error={errors.binding?.message}
          {...register("binding")}
        />
        {binding === "other" && (
          <TextField
            label="Describe the binding"
            required
            error={errors.binding_other?.message}
            {...register("binding_other")}
          />
        )}
        <Full>
          <TextArea label="Notes" error={errors.notes?.message} {...register("notes")} />
        </Full>
      </Fieldset>

      <fieldset className="rounded-lg border border-line bg-surface p-5">
        <legend className="px-1 text-lg font-bold">
          Attached files{" "}
          <span className="text-danger" aria-hidden="true">
            *
          </span>
        </legend>
        <div className="mt-2">
          <FileUploader
            maxBytes={config.upload_max_bytes}
            chunkBytes={config.upload_chunk_bytes}
            files={files}
            onChange={setFiles}
            error={fileError}
          />
        </div>
      </fieldset>

      <div className="flex flex-wrap gap-3">
        <Button type="submit">Review order</Button>
        <Button variant="secondary" onClick={saveDraft}>
          Save draft
        </Button>
        <Link
          to="/orders"
          className="inline-flex min-h-11 items-center px-3 text-brand-700 underline"
        >
          Cancel
        </Link>
      </div>
    </form>
  );
}
